// Terminology and imports (docs/plans/phase1-terminology-and-import.md §3, §4.5).
// Search text travels only in POST bodies, never in a URL.
import { queryOptions } from '@tanstack/react-query';
import {
  codeSystemListSchema,
  codeSystemVersionListSchema,
  codeSystemVersionSchema,
  errorBodySchema,
  IMPORT_CHUNK_BYTES,
  importIssuePageSchema,
  importJobSchema,
  importJobSummarySchema,
  importKindListSchema,
  pageSchema,
  terminologySearchResponseSchema,
  type StartImportBody,
  type TerminologyCoding,
  type TerminologySearchRequest,
} from '@wodi/shared';

import { api, ApiError, errorMessage, OFFLINE_MESSAGE } from './client.ts';

export const codeSystemsQuery = queryOptions({
  queryKey: ['terminology', 'code-systems'],
  queryFn: () => api('GET', '/terminology/code-systems', { schema: codeSystemListSchema }),
});

export function codeSystemVersionsQuery(key: string) {
  return queryOptions({
    queryKey: ['terminology', 'code-systems', key, 'versions'],
    queryFn: () =>
      api('GET', `/terminology/code-systems/${encodeURIComponent(key)}/versions`, {
        schema: codeSystemVersionListSchema,
      }),
  });
}

/** A search as the client sends it; the server fills the limit and national default. */
export type TerminologySearchInput = Pick<TerminologySearchRequest, 'systems' | 'query'> &
  Partial<Pick<TerminologySearchRequest, 'limit' | 'includeNational'>>;

export function searchTerminology(body: TerminologySearchInput) {
  return api('POST', '/terminology/search', { body, schema: terminologySearchResponseSchema });
}

export function activateVersion(key: string, versionId: string, reason: string) {
  return api(
    'POST',
    `/terminology/code-systems/${encodeURIComponent(key)}/versions/${versionId}/activate`,
    { body: { reason }, schema: codeSystemVersionSchema },
  );
}

export function recordRemoteSelection(coding: TerminologyCoding) {
  return api('POST', '/terminology/remote-selections', { body: coding });
}

// --- Imports ------------------------------------------------------------------------

export const importKindsQuery = queryOptions({
  queryKey: ['imports', 'kinds'],
  queryFn: () => api('GET', '/imports/kinds', { schema: importKindListSchema }),
});

export const importsQuery = queryOptions({
  queryKey: ['imports', 'list'],
  queryFn: () => api('GET', '/imports?limit=50', { schema: pageSchema(importJobSummarySchema) }),
});

export function importQuery(id: string) {
  return queryOptions({
    queryKey: ['imports', id],
    queryFn: () => api('GET', `/imports/${id}`, { schema: importJobSchema }),
  });
}

export function importIssuesQuery(id: string) {
  return queryOptions({
    queryKey: ['imports', id, 'issues'],
    queryFn: () => api('GET', `/imports/${id}/issues?limit=200`, { schema: importIssuePageSchema }),
  });
}

export function startImport(body: StartImportBody) {
  return api('POST', '/imports', { body, schema: importJobSchema });
}

/** Sends one part of the file as raw bytes. Sending the same part again is harmless. */
export async function putImportChunk(id: string, seq: number, bytes: Blob): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/imports/${id}/chunks/${String(seq)}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/octet-stream', accept: 'application/json' },
      body: bytes,
    });
  } catch {
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE);
  }
  if (response.ok) return;
  const parsed = errorBodySchema.safeParse(await response.json().catch(() => null));
  if (parsed.success) {
    const { code, message, requestId } = parsed.data.error;
    throw new ApiError(response.status, code, errorMessage(message, requestId), requestId);
  }
  throw new ApiError(response.status, 'unexpected_response', 'Something went wrong. Try again.');
}

export function finishUpload(id: string) {
  return api('POST', `/imports/${id}/uploaded`, { schema: importJobSchema });
}

export function commitImport(
  id: string,
  expectedValidRows: number,
  acknowledgementReason: string | null = null,
) {
  return api('POST', `/imports/${id}/commit`, {
    body: {
      expectedValidRows,
      acknowledgement: acknowledgementReason === null ? null : { reason: acknowledgementReason },
    },
    schema: importJobSchema,
  });
}

export function cancelImport(id: string, reason: string) {
  return api('POST', `/imports/${id}/cancel`, { body: { reason }, schema: importJobSchema });
}

/** The address of the problem report; a download, so a plain link. */
export function importReportUrl(id: string): string {
  return `/api/v1/imports/${id}/report.csv`;
}

export { IMPORT_CHUNK_BYTES };

/** SHA-256 of a file, as lowercase hex, computed in the browser. */
export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
